import {
  INTRANET_SHORTCUT_ICONS,
  IntranetLinkTone,
  type IntranetLink,
} from '@gb-ticket/shared-types';

type ShortcutTone = (typeof IntranetLinkTone)[keyof typeof IntranetLinkTone];
import { ArrowLeftIcon, Loader2Icon, PencilIcon, PlusIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import {
  useCreateIntranetLink,
  useDeleteIntranetLink,
  useIntranetLinkAudienceRoles,
  useIntranetLinks,
  useIntranetWidgets,
  useUpdateIntranetLink,
  useUpdateIntranetWidget,
} from '@/products/intranet/features/intranet/use-intranet-directory';
import { INTRANET_EDITORIAL_PATH } from '@/products/intranet/intranet-paths';
import { ModuleManageSection } from '@/products/intranet/pages/editorial/module-manage-section';

type Draft = {
  label: string;
  url: string;
  iconName: string;
  tone: ShortcutTone;
  sortOrder: string;
  enabled: boolean;
  audienceRoleIds: string[];
};

const EMPTY: Draft = {
  label: '',
  url: '',
  iconName: 'ExternalLink',
  tone: IntranetLinkTone.NAVY,
  sortOrder: '0',
  enabled: true,
  audienceRoleIds: [],
};

const TONE_LABEL: Record<ShortcutTone, string> = {
  navy: 'Navy',
  red: 'Rot',
  green: 'Grün',
  amber: 'Bernstein',
};

export function ShortcutsManagePage() {
  usePageTitle('Schnellzugriff');
  const links = useIntranetLinks();
  const audienceRoles = useIntranetLinkAudienceRoles();
  const widgets = useIntranetWidgets();
  const create = useCreateIntranetLink();
  const update = useUpdateIntranetLink();
  const remove = useDeleteIntranetLink();
  const updateWidget = useUpdateIntranetWidget();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IntranetLink | null>(null);
  const items = links.data?.items ?? [];
  const saving = create.isPending || update.isPending;

  function startEdit(link: IntranetLink) {
    setEditingId(link.id);
    setDraft({
      label: link.label,
      url: link.url,
      iconName: link.iconName ?? 'ExternalLink',
      tone: link.tone,
      sortOrder: String(link.sortOrder),
      enabled: link.enabled,
      audienceRoleIds: [...link.audienceRoleIds],
    });
  }

  function resetForm() {
    setEditingId(null);
    setDraft(EMPTY);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = {
      label: draft.label.trim(),
      url: draft.url.trim(),
      iconName: draft.iconName,
      tone: draft.tone,
      sortOrder: Number(draft.sortOrder) || 0,
      enabled: draft.enabled,
      audienceRoleIds: draft.audienceRoleIds,
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, body });
        toast.success('Schnellzugriff gespeichert.');
      } else {
        await create.mutateAsync(body);
        toast.success('Schnellzugriff angelegt.');
      }
      resetForm();
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function saveWidget(id: string, enabled: boolean, sortOrder: number) {
    try {
      await updateWidget.mutateAsync({ id, body: { enabled, sortOrder } });
      toast.success('Gadget gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-8">
      <PlatformPageHeader
        title="Schnellzugriff"
        description="Icon-Raster und Gadgets der Intranet-Startseite. Neue Einträge erscheinen ohne Deployment."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={INTRANET_EDITORIAL_PATH}>
              <ArrowLeftIcon aria-hidden />
              Zurück
            </Link>
          </Button>
        }
      />

      <form
        aria-label="Link-Kachel"
        className={`${intranetPanelClassName} grid gap-3 sm:grid-cols-2`}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="space-y-1">
          <Label htmlFor="shortcut-label">Bezeichnung</Label>
          <Input
            id="shortcut-label"
            value={draft.label}
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="shortcut-url">Pfad oder Adresse</Label>
          <Input
            id="shortcut-url"
            value={draft.url}
            placeholder="/intranet/wissensdatenbank oder https://…"
            onChange={(event) => setDraft({ ...draft, url: event.target.value })}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="shortcut-icon">Icon</Label>
          <Select
            value={draft.iconName}
            onValueChange={(value) => setDraft({ ...draft, iconName: value })}
          >
            <SelectTrigger id="shortcut-icon" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTRANET_SHORTCUT_ICONS.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="shortcut-tone">Farbe</Label>
          <Select
            value={draft.tone}
            onValueChange={(value) => setDraft({ ...draft, tone: value as ShortcutTone })}
          >
            <SelectTrigger id="shortcut-tone" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(IntranetLinkTone).map((tone) => (
                <SelectItem key={tone} value={tone}>
                  {TONE_LABEL[tone]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="shortcut-order">Reihenfolge</Label>
          <Input
            id="shortcut-order"
            inputMode="numeric"
            value={draft.sortOrder}
            onChange={(event) => setDraft({ ...draft, sortOrder: event.target.value })}
          />
        </div>
        <label className="flex min-h-11 items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
          />
          Sichtbar
        </label>
        <LinkAudienceChecklist
          roleIds={draft.audienceRoleIds}
          roles={audienceRoles.data?.items ?? []}
          pending={audienceRoles.isPending}
          onChange={(audienceRoleIds) => setDraft({ ...draft, audienceRoleIds })}
        />
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" disabled={saving}>
            {editingId ? <PencilIcon aria-hidden /> : <PlusIcon aria-hidden />}
            {editingId ? 'Speichern' : 'Anlegen'}
          </Button>
          {editingId ? (
            <Button type="button" variant="outline" onClick={resetForm}>
              Abbrechen
            </Button>
          ) : null}
        </div>
      </form>

      {links.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Einträge werden geladen …
        </p>
      ) : (
        <div className={intranetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Ziel</TableHead>
                <TableHead>Sichtbar</TableHead>
                <TableHead>Gruppen</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>{link.label}</TableCell>
                  <TableCell className="max-w-48 truncate">{link.url}</TableCell>
                  <TableCell>{link.enabled ? 'Ja' : 'Nein'}</TableCell>
                  <TableCell>
                    {link.audienceRoleIds.length === 0 ? 'Alle' : link.audienceRoleIds.length}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(link)}>
                      Bearbeiten
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPendingDelete(link)}
                    >
                      Löschen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ModuleManageSection />

      <section className="space-y-3" aria-labelledby="intranet-gadgets">
        <h2 id="intranet-gadgets" className="text-lg font-semibold">
          Gadgets
        </h2>
        <p className="text-sm text-muted-foreground">
          Nur die eingebauten Bausteine. Ein ausgeschaltetes Gadget verschwindet von der Startseite.
        </p>
        <ul className="space-y-2">
          {(widgets.data?.items ?? []).map((widget) => (
            <WidgetRow
              key={widget.id}
              id={widget.id}
              kind={widget.kind}
              enabled={widget.enabled}
              sortOrder={widget.sortOrder}
              pending={updateWidget.isPending}
              onSave={saveWidget}
            />
          ))}
        </ul>
      </section>

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title="Eintrag löschen?"
        description={pendingDelete ? `„${pendingDelete.label}“ wird entfernt.` : ''}
        pending={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) {
            return;
          }
          void remove.mutateAsync(pendingDelete.id).then(() => {
            toast.success('Eintrag gelöscht.');
            setPendingDelete(null);
          });
        }}
      />
    </section>
  );
}

function LinkAudienceChecklist({
  roleIds,
  roles,
  pending,
  onChange,
}: {
  roleIds: readonly string[];
  roles: readonly { id: string; name: string; isSystem: boolean }[];
  pending: boolean;
  onChange: (roleIds: string[]) => void;
}) {
  return (
    <fieldset className="space-y-2 sm:col-span-2">
      <legend className="text-sm font-medium">Sichtbarkeit</legend>
      <p className="text-xs text-muted-foreground">
        Ohne Auswahl sieht jede Person mit Intranet-Zugang die Kachel.
      </p>
      <div className={`${intranetTableFrameClassName} max-h-48 space-y-2 overflow-y-auto p-2`}>
        {pending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Gruppen werden geladen …
          </p>
        ) : null}
        {roles.map((role) => (
          <label
            key={role.id}
            className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
          >
            <Checkbox
              checked={roleIds.includes(role.id)}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...new Set([...roleIds, role.id])]
                    : roleIds.filter((id) => id !== role.id),
                )
              }
            />
            <span>
              <span className="font-medium">{role.name}</span>
              <span className="block text-xs text-muted-foreground">
                {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
              </span>
            </span>
          </label>
        ))}
        {!pending && roles.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Berechtigungsgruppen verfügbar.</p>
        ) : null}
      </div>
    </fieldset>
  );
}

function WidgetRow({
  id,
  kind,
  enabled,
  sortOrder,
  pending,
  onSave,
}: {
  id: string;
  kind: string;
  enabled: boolean;
  sortOrder: number;
  pending: boolean;
  onSave: (id: string, enabled: boolean, sortOrder: number) => Promise<void>;
}) {
  const [on, setOn] = useState(enabled);
  const [order, setOrder] = useState(String(sortOrder));
  return (
    <li className={`${intranetPanelClassName} flex flex-wrap items-center gap-3 px-3 py-2`}>
      <span className="min-w-28 text-sm font-medium">{kind}</span>
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" checked={on} onChange={(event) => setOn(event.target.checked)} />
        Sichtbar
      </label>
      <Input
        aria-label={`Reihenfolge ${kind}`}
        className="w-24"
        inputMode="numeric"
        value={order}
        onChange={(event) => setOrder(event.target.value)}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={pending}
        onClick={() => void onSave(id, on, Number(order) || 0)}
      >
        Speichern
      </Button>
    </li>
  );
}
